import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AiChatSessionKey } from 'pages/ai-chat/AiChatSessionProvider';

export type FloatingAiVisibility = 'closed' | 'open' | 'closing';

export interface AiChatState {
    /**
     * Visibility of the floating AI panel. Kept in the store because the full `/ai-chat` page opens
     * the panel from outside its subtree when an entity link navigates away from the page.
     * `closing` animates, so the panel outlives the click that dismissed it.
     */
    floatingVisibility: FloatingAiVisibility;
    /**
     * Which of the two layout-level sessions the panel is rendering. Normally its own, so the panel
     * and `/ai-chat` hold separate conversations; `page` while it is carrying a conversation handed
     * over from `/ai-chat`, which is how a running response keeps streaming across that navigation.
     */
    floatingSessionKey: AiChatSessionKey;
    /**
     * Thread just deleted, from either surface. Whoever is still pointing at it — a session showing
     * it, a URL naming it — has to let go, and the deleting surface is not always the one holding
     * it. Cleared once acted on.
     */
    deletedThreadId: number | null;
    /**
     * Set when opening the panel auto-resumed a previous conversation (the five-minute rule), so
     * the panel can say so in a banner. In the store because the policy runs where the panel is
     * owned while the banner is dismissed from inside it. Cleared by the interactions that make
     * the conversation the user's own again: sending, a new chat, or picking another thread.
     */
    floatingResume: { threadId: number; lastActiveAt: number } | null;
    /**
     * A question an opener outside the panel put into the panel's composer — the Help Center's
     * "ask AI about this page" and "ask about this search" do it. In the store because the opener
     * is never inside the panel's subtree. Not sent: the reader still edits and submits it. Held
     * only until the panel has copied it into its composer, which clears it at once; that is what
     * lets the same affordance seed the same question again, since the next click is a change from
     * null. Also cleared on close, so a later opening starts on an empty composer.
     */
    floatingPrompt: string | null;
}

const initialState: AiChatState = {
    floatingVisibility: 'closed',
    floatingSessionKey: 'floating',
    deletedThreadId: null,
    floatingResume: null,
    floatingPrompt: null,
};

const aiChatSlice = createSlice({
    name: 'aiChat',
    initialState,
    reducers: {
        setFloatingAiVisibility(state, action: PayloadAction<FloatingAiVisibility>) {
            state.floatingVisibility = action.payload;
            if (action.payload === 'closed') {
                // A borrowed conversation is only borrowed for as long as the panel stays open; it
                // is not lost, it is still the page's.
                state.floatingSessionKey = 'floating';
                // The resume note describes one opening. Kept past the close it would resurface on
                // the next open — which may well resume nothing — timestamped from the wrong one.
                state.floatingResume = null;
                // A question nobody asked is not waiting for the next opening.
                state.floatingPrompt = null;
            }
        },
        /**
         * Opens the panel with a question already typed, for an "ask AI about this" affordance. The
         * question goes to the panel's own session, never to a conversation handed over from
         * `/ai-chat`: that one stays the page's and keeps streaming there, as on a close.
         */
        openFloatingAiWithPrompt(state, action: PayloadAction<string>) {
            state.floatingVisibility = 'open';
            state.floatingSessionKey = 'floating';
            state.floatingPrompt = action.payload;
        },
        /**
         * The panel has put the seeded question in its composer. Left in the store, a second click
         * of the affordance that seeded it would hand over the same value and change nothing.
         */
        clearFloatingPrompt(state) {
            state.floatingPrompt = null;
        },
        /** Entity link followed out of `/ai-chat`: the panel picks that conversation up mid-stream. */
        openFloatingAiWithPageSession(state) {
            state.floatingVisibility = 'open';
            state.floatingSessionKey = 'page';
        },
        /** Panel switched conversations on its own, so it stops speaking for the page. */
        switchFloatingToOwnSession(state) {
            state.floatingSessionKey = 'floating';
            // Both ways here — a new chat or picking a thread — are the user taking over, which
            // ends the "this was resumed for you" message.
            state.floatingResume = null;
        },
        /** Opening the panel restored the recent conversation instead of starting fresh. */
        floatingAiAutoResumed(state, action: PayloadAction<{ threadId: number; lastActiveAt: number }>) {
            state.floatingResume = action.payload;
        },
        clearFloatingResume(state) {
            state.floatingResume = null;
        },
        aiChatThreadDeleted(state, action: PayloadAction<number>) {
            state.deletedThreadId = action.payload;
        },
        clearDeletedThread(state) {
            state.deletedThreadId = null;
        },
        /**
         * `/ai-chat` mounting takes its conversation back: the full page shows the thread that was
         * running there, so the panel closes and stops borrowing. The panel's own conversation is
         * untouched — reopening it returns to that, and its history can move it elsewhere.
         */
        reclaimPageSession(state) {
            state.floatingVisibility = 'closed';
            state.floatingSessionKey = 'floating';
            state.floatingResume = null;
            state.floatingPrompt = null;
        },
    },
});

export const {
    setFloatingAiVisibility,
    openFloatingAiWithPrompt,
    clearFloatingPrompt,
    openFloatingAiWithPageSession,
    switchFloatingToOwnSession,
    reclaimPageSession,
    aiChatThreadDeleted,
    clearDeletedThread,
    floatingAiAutoResumed,
    clearFloatingResume,
} = aiChatSlice.actions;
export default aiChatSlice.reducer;
